import { alpha, Box, Checkbox, styled as muiStyled, Stack } from '@mui/material';
import { abyssColors, primary } from '@/theme/colors';

type DetailsProps = {
  columnCount?: number;
  'data-testid'?: string;
};

export const Card = muiStyled(Stack)(({ selected = false }: { selected: boolean }) => ({
  padding: '1.2rem',
  paddingRight: '1.6rem',
  borderRadius: '0.8rem',
  border: `0.1rem solid ${alpha(primary.gray, 0.5)}`,
  width: '100%',
  flexDirection: 'row',
  gap: '2.8rem',
  minHeight: '19.2rem',
  position: 'relative',
  marginBottom: '1.6rem',
  backgroundColor: selected ? abyssColors.primary[50] : 'transparent',
  transition: 'background-color 0.2s ease-in-out',
}));

export const CardSkeleton = muiStyled(Stack)(() => ({
  padding: '1.2rem',
  paddingRight: '1.6rem',
  borderRadius: '0.8rem',
  border: `0.1rem solid ${alpha(primary.gray, 0.2)}`,
  width: '100%',
  flexDirection: 'row',
  gap: '2.8rem',
  minHeight: '19.2rem',
  position: 'relative',
  backgroundColor: alpha(abyssColors.primary[50], 0.3),
  '& .MuiSkeleton-root': { background: 'transparent' },
}));

export const Header = muiStyled(Stack)(() => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  height: 'fit-content',
}));
export const Field = muiStyled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  height: 'fit-content',
  breakInside: 'avoid',
  justifyContent: 'space-between',
  marginBottom: '0.4rem',
}));

export const Details = muiStyled(Box)<DetailsProps>(({ columnCount = 2 }) => ({
  columnCount,
  columnGap: '4rem', // space between columns
  position: 'relative',
  // Create a fake center border using a background gradient
  background: `linear-gradient(
              to right,
              transparent 49.5%,
              #F0F0F0 49.5%,
              #F0F0F0 50.5%,
              transparent 50.5%
            )`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundSize: columnCount === 2 ? '0.1rem 100%' : '0',
  width: columnCount === 2 ? '75%' : '35%',
  // Add data attribute for testing
  '&': {
    'data-column-count': columnCount,
  },
}));
export const Tile = muiStyled(Stack)(() => ({
  width: '100%',
  overflow: 'hidden',
  background: abyssColors.neutral[50],
  border: `0.1rem solid ${abyssColors.primary[100]}`,
  padding: '1.2em 1.6rem',
  gap: '0.8rem',
  borderRadius: 0,
  alignItems: 'center',
  justifyContent: 'space-between',
  flexDirection: 'row',
  marginBottom: '1rem',
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    border: `0.1rem solid ${abyssColors.primary[200]}`,
  },
}));

type CustomCheckboxProps = {
  checkboxSize?: 'lg' | 'md' | 'sm';
};

export const StyledCheckbox = muiStyled(Checkbox, {
  shouldForwardProp: (prop) => prop !== 'checkboxSize',
})<CustomCheckboxProps>(({ checkboxSize = 'lg' }) => ({
  color: alpha(primary.light, 0.5),
  padding: 0,
  margin: 0,
  transition: 'color 0.3s ease-in-out, transform 0.2s ease-in-out',
  '&.Mui-checked': {
    color: abyssColors.primary[500],
  },
  '& .MuiSvgIcon-root': {
    fontSize: checkboxSize === 'lg' ? '2.4rem' : checkboxSize === 'md' ? '2rem' : '1.6rem',
    transition: 'color 0.3s ease-in-out, transform 0.2s ease-in-out',
  },
}));
